// Minimal Markdown ↔ HTML converter + WYSIWYG-friendly serializer.
// Scope: headings (#,##,###), bold, italic, inline code, code blocks, unordered/ordered lists, blockquotes, links, images, hr, paragraphs, line breaks.
// Ported verbatim from prototype/md.jsx; uses DOM types available in browser.

// ----- Markdown → HTML -----

// @ts-nocheck — this helper was authored in untyped JS; typing it fully is out of scope.
//   The browser runtime behavior is unchanged from the prototype.

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Keep only schemes safe to render in href/src. Blocks javascript:, data: (exfil/XSS),
// vbscript:, file:, etc. Relative paths (./, /foo, #anchor, no scheme) are allowed.
function safeUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '#';
  // Detect scheme: letters/digits/+/-/. then colon (per RFC 3986). No colon → relative, safe.
  const m = raw.match(/^([a-zA-Z][a-zA-Z0-9+\-.]*):/);
  if (!m) return raw;
  const scheme = m[1].toLowerCase();
  const allowed = new Set(['http', 'https', 'mailto', 'tel']);
  return allowed.has(scheme) ? raw : '#';
}

function renderInline(text) {
  // order matters: images first (they contain [], then links)
  let s = escapeHtml(text);
  // images ![alt](url)
  s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (m, alt, url) => `<img src="${safeUrl(url)}" alt="${alt}">`);
  // links [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, text, url) => `<a href="${safeUrl(url)}" target="_blank" rel="noopener">${text}</a>`);
  // bold **x**
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  // italic _x_ or *x* (avoid eating bold)
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  s = s.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>');
  // inline code
  s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  return s;
}

function mdToHtml(md) {
  if (!md) return '';
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;

  const flushParagraph = (buf) => {
    const text = buf.join(' ').trim();
    if (text) out.push(`<p>${renderInline(text)}</p>`);
  };

  while (i < lines.length) {
    let line = lines[i];

    // blank line
    if (/^\s*$/.test(line)) { i++; continue; }

    // hr
    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) { out.push('<hr>'); i++; continue; }

    // heading
    const h = line.match(/^(#{1,6})\s+(.+)$/);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level}>${renderInline(h[2].trim())}</h${level}>`);
      i++;
      continue;
    }

    // fenced code block
    if (/^```/.test(line)) {
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++; // skip closing ```
      out.push(`<pre><code>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }

    // blockquote (1+ lines starting with >)
    if (/^\s*>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      out.push(`<blockquote><p>${renderInline(buf.join(' '))}</p></blockquote>`);
      continue;
    }

    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ''));
        i++;
      }
      out.push('<ul>' + items.map(it => `<li>${renderInline(it)}</li>`).join('') + '</ul>');
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      out.push('<ol>' + items.map(it => `<li>${renderInline(it)}</li>`).join('') + '</ol>');
      continue;
    }

    // paragraph: collect until blank / block-starter
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,6}\s|\s*[-*+]\s|\s*\d+\.\s|\s*>\s|```|---|\*\*\*|___)/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    flushParagraph(buf);
  }

  return out.join('\n');
}

// ----- HTML → Markdown -----
// For contentEditable round-tripping. Walks the DOM and serializes.

function htmlToMd(html) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  return serializeChildren(wrapper).trim();
}

function serializeChildren(node) {
  let out = '';
  node.childNodes.forEach(child => {
    out += serializeNode(child);
  });
  return out;
}

function serializeInline(node) {
  let out = '';
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent;
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.tagName.toLowerCase();
    const inner = serializeInline(child);
    if (tag === 'strong' || tag === 'b') out += `**${inner}**`;
    else if (tag === 'em' || tag === 'i') out += `_${inner}_`;
    else if (tag === 'code') out += `\`${inner}\``;
    else if (tag === 'a') out += `[${inner}](${child.getAttribute('href') || ''})`;
    else if (tag === 'img') out += `![${child.getAttribute('alt') || ''}](${child.getAttribute('src') || ''})`;
    else if (tag === 'br') out += '\n';
    else out += inner;
  });
  return out;
}

function serializeNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = node.textContent;
    if (t.trim() === '') return '';
    return t + '\n\n';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const tag = node.tagName.toLowerCase();
  const inner = serializeInline(node).trim();

  if (/^h[1-6]$/.test(tag)) {
    const level = parseInt(tag[1]);
    return '#'.repeat(level) + ' ' + inner + '\n\n';
  }
  if (tag === 'p') return inner + '\n\n';
  if (tag === 'hr') return '---\n\n';
  if (tag === 'blockquote') {
    const inside = serializeChildren(node).trim();
    return inside.split('\n').map(l => '> ' + l).join('\n') + '\n\n';
  }
  if (tag === 'ul') {
    let s = '';
    node.querySelectorAll(':scope > li').forEach(li => {
      s += '- ' + serializeInline(li).trim() + '\n';
    });
    return s + '\n';
  }
  if (tag === 'ol') {
    let s = '';
    let n = 1;
    node.querySelectorAll(':scope > li').forEach(li => {
      s += (n++) + '. ' + serializeInline(li).trim() + '\n';
    });
    return s + '\n';
  }
  if (tag === 'pre') {
    const code = node.textContent;
    return '```\n' + code + '\n```\n\n';
  }
  if (tag === 'div') {
    // contentEditable wraps lines in divs — treat as paragraph if it has text.
    if (inner) return inner + '\n\n';
    return '';
  }
  if (tag === 'img') return `![${node.getAttribute('alt') || ''}](${node.getAttribute('src') || ''})\n\n`;
  return inner;
}

// Short plain-text preview from MD (first N chars, stripped of syntax)
function mdPreview(md, charLimit = 140) {
  if (!md) return '';
  let s = md
    .replace(/^#+\s+/gm, '')           // headings
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/\*\*|__|\*|_|`/g, '')     // emphasis markers
    .replace(/^>\s?/gm, '')             // blockquote markers
    .replace(/^[-*+]\s+/gm, '• ')       // list items
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^(---|\*\*\*|___)$/gm, '') // hr
    .replace(/\n{2,}/g, ' · ')
    .replace(/\n/g, ' ')
    .trim();
  if (s.length > charLimit) s = s.slice(0, charLimit).trim() + '…';
  return s;
}

export { mdToHtml, htmlToMd, mdPreview };
